// Pure helpers for HouseholdMergeReview — the per-field household
// merge UX, parallel to MergeReviewWizard for contacts.
//
// No "Keep both" affordance: households don't have a secondary slot
// for any of their mergeable fields. The picker is just per-field
// star → "primary" or "merged".
//
// Kept React-free + side-effect-free so tests in
// `tests/web/household-merge-review-helpers.test.ts` exercise the
// override-composition logic without standing up DOM / D1.

import { normalizeDisplayName } from "../../../shared/lib/normalize-display-name";
import type {
  HouseholdFieldOverrides,
  HouseholdMergeableField,
} from "../../../shared/db/household-merge";

// Local structural type — the helper takes a household-shaped object
// (the API `Household` type, but typed loosely so the test file can
// build a mock without importing it).
export type HouseholdMergeShape = Readonly<
  Partial<Record<HouseholdMergeableField, unknown>>
>;

// Display order for the household merge review table. Matches the
// shape of MERGEABLE_FIELDS_UI from contact merge.
export const HOUSEHOLD_MERGEABLE_FIELDS_UI: ReadonlyArray<{
  key: HouseholdMergeableField;
  label: string;
}> = [
  { key: "display_name", label: "Display name" },
  { key: "primary_contact_id", label: "Primary contact" },
  { key: "primary_street_line_1", label: "Mailing street" },
  { key: "primary_street_line_2", label: "Mailing apt / unit" },
  { key: "primary_city", label: "Primary city" },
  { key: "primary_state", label: "Primary state" },
  { key: "primary_zip", label: "Primary ZIP" },
  { key: "physical_street_line_1", label: "Physical street" },
  { key: "physical_street_line_2", label: "Physical apt / unit" },
  { key: "physical_city", label: "Physical city" },
  { key: "physical_state", label: "Physical state" },
  { key: "physical_zip", label: "Physical ZIP" },
  { key: "primary_county", label: "Mailing county" },
  { key: "physical_county", label: "Physical county" },
  { key: "notes", label: "Notes" },
  // Trust name and date rows left 2026-10-15: trusts are their own
  // records, carried to the survivor by the merge itself.
];

export type HouseholdPerFieldChoice = "primary" | "merged";
export type HouseholdPerFieldChoiceMap = Partial<
  Record<HouseholdMergeableField, HouseholdPerFieldChoice>
>;

/** Treat null / undefined / empty-string as blank. */
export function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}

/** Different for merge purposes: blank-vs-blank doesn't count. */
export function valuesDiffer(a: unknown, b: unknown): boolean {
  if (isBlank(a) && isBlank(b)) return false;
  return a !== b;
}

/**
 * Subset of household-mergeable fields where survivor and merged
 * differ. Conflicts-only view is the wizard's default.
 */
export function detectHouseholdConflicts(
  survivor: HouseholdMergeShape,
  merged: HouseholdMergeShape,
): HouseholdMergeableField[] {
  const conflicts: HouseholdMergeableField[] = [];
  for (const f of HOUSEHOLD_MERGEABLE_FIELDS_UI) {
    const s = (survivor as Record<string, unknown>)[f.key];
    const m = (merged as Record<string, unknown>)[f.key];
    if (valuesDiffer(s, m)) conflicts.push(f.key);
  }
  return conflicts;
}

/**
 * Smart default for a single field: if survivor is blank but merged
 * has a value, propose "merged". Otherwise default "primary".
 */
export function defaultHouseholdPerFieldChoice(
  survivorValue: unknown,
  mergedValue: unknown,
): HouseholdPerFieldChoice {
  if (isBlank(survivorValue) && !isBlank(mergedValue)) return "merged";
  return "primary";
}

/**
 * Build the full default per-field choice map for a survivor/merged
 * household pair.
 */
export function buildHouseholdDefaultChoices(
  survivor: HouseholdMergeShape,
  merged: HouseholdMergeShape,
): HouseholdPerFieldChoiceMap {
  const choices: HouseholdPerFieldChoiceMap = {};
  for (const f of HOUSEHOLD_MERGEABLE_FIELDS_UI) {
    const s = (survivor as Record<string, unknown>)[f.key];
    const m = (merged as Record<string, unknown>)[f.key];
    choices[f.key] = defaultHouseholdPerFieldChoice(s, m);
  }
  return choices;
}

/**
 * Turn the wizard's choice map into the shape expected by the
 * `/api/admin/households/:id/merge` endpoint. Only emits keys for
 * fields the user explicitly tagged "merged" — "primary" stays out
 * because it's the backend default and emitting it would just add
 * noise to the audit context.
 */
export function composeHouseholdFieldOverrides(
  choices: HouseholdPerFieldChoiceMap,
): HouseholdFieldOverrides {
  const overrides: HouseholdFieldOverrides = {};
  for (const [rawField, choice] of Object.entries(choices)) {
    const field = rawField as HouseholdMergeableField;
    if (choice === "merged") overrides[field] = "merged";
  }
  return overrides;
}

// ─── Primary-contact name resolution ────────────────────────────────
// The `primary_contact_id` merge field stores a raw contact UUID. Left
// unresolved, the review table shows an unreadable id (Baker, 2026-07-14:
// "the Primary contact row shows a string of random characters"). These
// helpers turn that id into the person's display name using the contacts
// the household-detail fetch already returned — no extra network call.

// Minimal member shape needed to build a display name. Structurally a
// subset of the API `HouseholdContact` so callers can pass those rows
// directly; typed loosely so the test file needs no API import.
export type ContactNameSource = {
  id: string;
  preferred_name?: string | null;
  legal_first_name?: string | null;
  legal_last_name?: string | null;
};

/**
 * Human-readable name for a household member — preferred name if set,
 * else legal first + last — run through the all-caps→display normalizer
 * (Grant's "no AC all-caps in the UI" rule). Returns "" when the member
 * has no usable name, so callers can fall back to the raw id.
 */
export function contactMergeDisplayName(c: ContactNameSource): string {
  const preferred = (c.preferred_name ?? "").trim();
  const legal = [c.legal_first_name, c.legal_last_name]
    .filter((x): x is string => Boolean(x && x.trim()))
    .join(" ")
    .trim();
  const name = preferred || legal;
  return name ? normalizeDisplayName(name) || name : "";
}

/**
 * Build a contact-id → display-name lookup from one or more member
 * lists (typically both merging households' contacts). Members with no
 * usable name are skipped so the review falls back to the raw id rather
 * than rendering a blank cell.
 */
export function buildContactNameMap(
  ...contactLists: ReadonlyArray<ReadonlyArray<ContactNameSource>>
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const list of contactLists) {
    for (const c of list) {
      const name = contactMergeDisplayName(c);
      if (name) map[c.id] = name;
    }
  }
  return map;
}

// ─── Member pairing options ─────────────────────────────────────────
// Feeds the "pair two people the matcher missed" dropdowns in the merge
// review (Grant, 2026-09-17). The two spellings that made a person two
// records are unpredictable, so the matcher will always miss some and a
// person needs a way to say "these two are the same man" by hand.

export interface MergeMemberOption {
  id: string;
  label: string;
}

/** Members of one household as picker options, name first, id as the
 *  fallback label so a nameless record is still selectable. */
export function buildMemberOptions(
  contacts: ReadonlyArray<ContactNameSource>,
): MergeMemberOption[] {
  return contacts.map((c) => ({
    id: c.id,
    label: contactMergeDisplayName(c) || c.id,
  }));
}

export type MemberMergeResult = {
  survivor_contact_id: string;
  merged_contact_id: string;
  merged: boolean;
  error?: string;
};

/**
 * Plain-English lines for the people the household merge could NOT fold
 * together. The households still merged, so silence here would leave the
 * user believing two records became one when they did not.
 */
export function describeFailedMemberMerges(
  outcomes: ReadonlyArray<MemberMergeResult>,
  nameById: Record<string, string>,
): string[] {
  return outcomes
    .filter((o) => !o.merged)
    .map((o) => {
      const keep = nameById[o.survivor_contact_id] ?? "one record";
      const fold = nameById[o.merged_contact_id] ?? "the other record";
      const why = o.error ? ` (${o.error})` : "";
      return `${keep} and ${fold} were left as two separate records${why}.`;
    });
}
